import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/components/AuthProvider";
import { useCustomer } from "@/integrations/autumn/client";
import { TrialStatus } from "@/integrations/autumn/types";
import { supabase } from "@/integrations/supabase/client";
import { getAffiliateCookie } from "@/lib/affiliate-tracker";
import { setAnalyticsUserProperties } from "@/lib/analytics";

export function useAutumnSubscription() {
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [legacyLifetimeAccess, setLegacyLifetimeAccess] = useState<boolean | null>(null);
  const { toast } = useToast();
  const { session } = useAuth();
  const { customer, checkout, openBillingPortal, allowed } = useCustomer();

  // Check for legacy lifetime access from database
  useEffect(() => {
    const checkLegacyAccess = async () => {
      if (!session?.user?.id) return;
      if (legacyLifetimeAccess !== null) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('has_lifetime_access')
          .eq('id', session.user.id)
          .single();

        if (error) {
          setLegacyLifetimeAccess(false);
          return;
        }

        setLegacyLifetimeAccess(data?.has_lifetime_access || false);
      } catch {
        setLegacyLifetimeAccess(false);
      }
    };

    checkLegacyAccess();
  }, [session?.user?.id, legacyLifetimeAccess]);

  // GA4: Set plan_type user property when Autumn customer data loads
  useEffect(() => {
    if (!customer?.products) return;
    const activeProduct = customer.products.find((p: any) =>
      p.status === 'active' || p.status === 'trialing'
    );
    const planType = activeProduct?.id === 'monthly' ? 'monthly'
      : activeProduct?.id === 'yearly' ? 'yearly'
      : 'none';
    setAnalyticsUserProperties({ plan_type: planType });
  }, [customer?.products]);

  // Sync Autumn subscription state to the profiles table so that the
  // server-side RPC (get_owner_subscription_info) can detect active
  // subscriptions for public flow access checks.
  //
  // We also mirror trial status into the legacy is_in_trial / trial_ends_at
  // columns because send-trial-emails filters on trial_ends_at.
  //
  // This runs every time customer.products changes (no ref guard) so trial
  // conversions to paid, plan changes, and renewals all propagate.
  useEffect(() => {
    const syncSubscriptionToProfile = async () => {
      if (!session?.user?.id || !customer?.products) return;

      const activeProduct = customer.products.find(
        (p: any) => p.status === 'active' || p.status === 'trialing'
      );

      if (!activeProduct) return;

      const trialEndsAt = (customer as any)?.trialStatus?.trialEndsAt;
      const periodEnd = (activeProduct as any).currentPeriodEnd || (activeProduct as any).current_period_end;
      const isTrialing = (activeProduct as any).status === 'trialing';

      let subscriptionActiveUntil: string;
      if (isTrialing && trialEndsAt) {
        subscriptionActiveUntil = new Date(
          typeof trialEndsAt === 'number' ? trialEndsAt * 1000 : trialEndsAt
        ).toISOString();
      } else if (periodEnd) {
        subscriptionActiveUntil = new Date(
          typeof periodEnd === 'number' ? periodEnd * 1000 : periodEnd
        ).toISOString();
      } else {
        // No period end available — use a short window so the record doesn't
        // stick if the sub is actually gone. Autumn webhook should replace
        // this with the authoritative value.
        subscriptionActiveUntil = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      }

      const update: Record<string, any> = {
        subscription_active_until: subscriptionActiveUntil,
        is_in_trial: isTrialing,
      };

      // Mirror trial_ends_at so the existing trial-email cron can find
      // Autumn-origin trials. Only write trial_started_at if not already set
      // to avoid clobbering the real trial start.
      if (isTrialing) {
        update.trial_ends_at = subscriptionActiveUntil;
        const { data: existing } = await supabase
          .from('profiles')
          .select('trial_started_at')
          .eq('id', session.user.id)
          .single();
        if (!existing?.trial_started_at) {
          update.trial_started_at = new Date().toISOString();
        }
      }

      try {
        await supabase
          .from('profiles')
          .update(update)
          .eq('id', session.user.id);
      } catch {
        // Non-critical — don't block the user
      }
    };

    syncSubscriptionToProfile();
    // We depend on `customer` (not just customer.products) because we also
    // read customer.trialStatus inside.
  }, [session?.user?.id, customer]);

  const startSubscription = async (planId: string) => {
    try {
      setIsCheckoutLoading(true);

      if (!session?.access_token) {
        throw new Error("Not authenticated");
      }

      // Check if user already has access before trying to purchase
      if (hasAccess(planId)) {
        toast({
          title: "You already have access!",
          description: "Redirecting to your dashboard...",
        });
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1000);
        return;
      }

      // Validate plan ID
      if (!['monthly', 'yearly'].includes(planId)) {
        throw new Error('Invalid plan ID. Must be "monthly" or "yearly"');
      }

      // Check legacy access first
      if (legacyLifetimeAccess === true) {
        toast({
          title: "You already have lifetime access!",
          description: "No need to subscribe. Redirecting to dashboard...",
        });
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1500);
        return;
      }

      // Get affiliate code from cookie if present
      const affiliateCode = getAffiliateCookie();

      // Persist the affiliate code to the profile so the Autumn webhook
      // handler can find it when the paid conversion fires (and doesn't
      // have to rely on the cookie still being present on the other side
      // of the Stripe Checkout redirect).
      //
      // Only write if non-null to avoid clobbering a previously-attributed
      // code with a null (users can click an affiliate link, then arrive
      // again via a non-affiliate path before converting).
      if (affiliateCode) {
        await supabase
          .from('profiles')
          .update({ affiliate_code: affiliateCode })
          .eq('id', session.user.id)
          .is('affiliate_code', null);  // only if not already attributed
      }

      const checkoutSessionParams: Record<string, any> = {};
      if (affiliateCode) {
        checkoutSessionParams.metadata = { affiliate_code: affiliateCode };
        checkoutSessionParams.payment_intent_data = {
          metadata: { affiliate_code: affiliateCode },
        };
      }

      // Call checkout() directly. Autumn's checkout() creates a Stripe
      // Checkout session with the trial period configured on the product,
      // and redirects the user. Do NOT call attach() here — attach()
      // subscribes the customer immediately and charges them if a payment
      // method is on file, bypassing the trial and the consent screen.
      await checkout({
        productId: planId,
        successUrl: `${window.location.origin}/dashboard?payment=success`,
        cancelUrl: `${window.location.origin}/dashboard?canceled=true`,
        checkoutSessionParams: Object.keys(checkoutSessionParams).length > 0
          ? checkoutSessionParams
          : undefined,
      });

      toast({
        title: "Redirecting to checkout...",
        description: "Please wait while we prepare your subscription.",
      });
    } catch (error: any) {
      console.error("Subscription error:", error);

      if (error?.message?.includes("already attached")) {
        toast({
          title: "You already have access!",
          description: "Redirecting to your dashboard...",
        });
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1000);
        return;
      }

      toast({
        title: "Error",
        description: error?.message || "Failed to start subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const getBillingPortalUrl = async (returnUrl?: string) => {
    try {
      if (!session?.access_token || !customer?.id) {
        throw new Error("Not authenticated or no customer ID");
      }

      await openBillingPortal({
        returnUrl: returnUrl || `${window.location.origin}/account`
      });

      toast({
        title: "Billing Portal Opened",
        description: "You can now manage your subscription and view invoices.",
      });
    } catch (error) {
      console.error("Billing portal error:", error);
      toast({
        title: "Error",
        description: "Failed to open billing portal. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const hasAccess = (productId: string = "lifetime") => {
    // Don't grant access if customer data hasn't loaded yet
    if (!customer) return false;

    const hasActiveOrTrial = customer?.products?.some((p: any) =>
      p.id === productId && (p.status === 'trialing' || p.status === 'active')
    );

    const hasAutumnAccess = allowed ? allowed({ productId }) : false;

    return hasAutumnAccess || hasActiveOrTrial;
  };

  const isInTrial = () => {
    return (customer as any)?.trialStatus?.isInTrial || false;
  };

  const getTrialStatus = (): TrialStatus | undefined => {
    return (customer as any)?.trialStatus;
  };

  const getDaysRemaining = (): number => {
    return (customer as any)?.trialStatus?.daysRemaining || 0;
  };

  const getTrialEndsAt = (): string | undefined => {
    return (customer as any)?.trialStatus?.trialEndsAt;
  };

  const hasLegacyLifetimeAccess = (): boolean => {
    return legacyLifetimeAccess === true;
  };

  // Loading means: Autumn customer data is still loading OR we're mid-checkout.
  // Consumers (RequireSubscription) should wait on this before redirecting.
  const isCustomerLoaded = customer !== undefined;
  // Full access readiness: Autumn customer is in AND we've checked the profiles
  // table for legacy lifetime access. Without this, gates that fall back to
  // hasValidAccess() can briefly classify a paid lifetime owner as "none".
  const isAccessLoaded = isCustomerLoaded && legacyLifetimeAccess !== null;
  const isLoading = isCheckoutLoading || !isCustomerLoaded;

  return {
    startSubscription,
    getBillingPortalUrl,
    hasAccess,
    isInTrial,
    getTrialStatus,
    getDaysRemaining,
    getTrialEndsAt,
    hasLegacyLifetimeAccess,
    isLoading,
    customer,
    isCustomerLoaded,
    isAccessLoaded,
  };
}
